import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FiBook, FiAward, FiUser, FiBarChart2,
  FiArrowLeft, FiCheckCircle, FiXCircle, FiZap, FiLock,
  FiClock, FiTarget, FiTrendingUp, FiStar
} from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { getLearnerMenu } from '../../components/layout/menuConfigs';
import EducationalParticles from '../../components/common/EducationalParticles';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';
import { showToast } from '../../components/common/Toaster';
import { useTheme } from '../../contexts/ThemeContext';
import { useCourseOwnership } from '../../contexts/CourseOwnershipContext';
import { learnerAIAPI } from '../../api/learnerEndpoints';
import { 
  DashboardContainer, 
  DashboardHeader,
  EmptyState,
} from '../dashboards/Dashboard.styles';

const QuizReviewPage = () => {
  const { quizAttemptId } = useParams();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { ownsCourse } = useCourseOwnership();
  const [courseId, setCourseId] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [reviewData, setReviewData] = useState(null);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const [aiExplanations, setAiExplanations] = useState({});
  const [loadingExplanations, setLoadingExplanations] = useState({});
  const [isExplanationModalOpen, setIsExplanationModalOpen] = useState(false);
  const [failedImages, setFailedImages] = useState(new Set());
  const [modalData, setModalData] = useState({
    questionId: null,
    questionText: '',
    userSelected: null,
    correctOption: null,
    message: ''
  });

  const menuItems = getLearnerMenu();

  useEffect(() => {
    if (quizAttemptId) {
      fetchQuizReview();
    }
  }, [quizAttemptId]);

  const fetchQuizReview = async () => {
    try {
      setLoading(true);
      const response = await learnerAIAPI.getQuizReview(quizAttemptId);
      setReviewData(response.data);
      // Extract course ID from review data if available
      if (response.data?.quiz?.course_id) {
        setCourseId(response.data.quiz.course_id);
      }
    } catch (error) {
      console.error('Error fetching quiz review:', error);
      if (error.response?.status === 403 && error.response?.data?.purchase_required) {
        showToast('warning', 'You need to purchase this course to access quiz reviews');
        navigate('/learner/browse');
      } else {
        showToast('error', 'Failed to load quiz review');
        navigate('/learner');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleQuestion = (questionId) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const fetchAIExplanation = async (questionId) => {
    // Check course ownership first
    if (courseId && !ownsCourse(courseId)) {
      showToast('warning', 'You need to purchase this course to access AI explanations');
      return;
    }

    // If already loaded, open modal directly
    if (aiExplanations[questionId]) {
      setModalData(prev => ({ ...prev, questionId, message: aiExplanations[questionId] }));
      setIsExplanationModalOpen(true);
      return;
    }

    try {
      setLoadingExplanations(prev => ({ ...prev, [questionId]: true }));
      const response = await learnerAIAPI.getAIExplanation(questionId, quizAttemptId);

      const message = response.data.message || '';
      setAiExplanations(prev => ({
        ...prev,
        [questionId]: message
      }));

      setModalData(prev => ({
        ...prev,
        questionId,
        message,
        userSelected: response.data.user_selected || null,
        correctOption: response.data.correct_option || null,
        questionText: response.data.question_text || ''
      }));
      setIsExplanationModalOpen(true);
      
      if (response.data.cached) {
        showToast('info', 'Explanation loaded from cache');
      }
    } catch (error) {
      console.error('Error fetching AI explanation:', error);
      if (error.response?.status === 403 && error.response?.data?.purchase_required) {
        showToast('warning', 'Course purchase required for AI explanations');
      } else {
        showToast('error', error.response?.data?.error || 'Failed to get AI explanation');
      }
    } finally {
      setLoadingExplanations(prev => ({ ...prev, [questionId]: false }));
    }
  };

  const getLetterForIndex = (index) => {
    return String.fromCharCode(65 + index); // A, B, C, D, etc.
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return '#10B981';
    if (percentage >= 60) return '#F59E0B';
    return '#EF4444';
  };

  const renderFormattedMessage = (message) => {
    if (!message) return null;
    const blocks = message.split(/\n\n+/);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {blocks.map((block, idx) => {
          const lines = block.split(/\n/).map(l => l.trim()).filter(Boolean);
          const looksLikeList = lines.length > 1 && lines.every(l => /^(-|\d+\.|\d+\))/i.test(l));
          if (looksLikeList) {
            return (
              <ul key={idx} style={{ margin: 0, paddingLeft: '20px', lineHeight: 1.8 }}>
                {lines.map((l, i) => (
                  <li key={i}>{l.replace(/^(-|\d+\.|\d+\))\s*/, '')}</li>
                ))}
              </ul>
            );
          }
          return (
            <p key={idx} style={{ margin: 0, lineHeight: 1.8 }}>{block}</p>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={menuItems}>
        <DashboardContainer>
          <LoadingSpinner size="large" text="Loading quiz review..." />
        </DashboardContainer>
      </DashboardLayout>
    );
  }

  if (!reviewData) {
    return (
      <DashboardLayout menuItems={menuItems}>
        <DashboardContainer>
          <EmptyState>
            <FiTarget size={48} />
            <h3>Quiz Review Not Found</h3>
            <p>The quiz review you're looking for doesn't exist</p>
            <Button onClick={() => navigate('/learner')}>
              Go to Dashboard
            </Button>
          </EmptyState>
        </DashboardContainer>
      </DashboardLayout>
    );
  }

  const { quiz_attempt, questions } = reviewData;
  const scoreColor = getScoreColor(quiz_attempt.percentage);

  return (
    <DashboardLayout menuItems={menuItems}>
      <EducationalParticles />
      <DashboardContainer>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <FiArrowLeft /> Back
          </Button>
        </div>

        {/* Performance Card */}
        <div style={{
          background: `linear-gradient(135deg, ${scoreColor}15 0%, ${scoreColor}05 100%)`,
          border: `2px solid ${scoreColor}40`,
          borderRadius: '16px',
          padding: '32px',
          marginBottom: '32px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative Background */}
          <div style={{
            position: 'absolute',
            top: '-50px',
            right: '-50px',
            width: '200px',
            height: '200px',
            background: `${scoreColor}10`,
            borderRadius: '50%',
            opacity: 0.5
          }} />
          
          <div style={{ position: 'relative', zIndex: 1 }}>
            <DashboardHeader style={{ marginBottom: '24px' }}>
              <div>
                <h1 style={{ marginBottom: '8px' }}>
                  {quiz_attempt.passed ? '🎉 Congratulations!' : '📚 Keep Practicing!'}
                </h1>
                <p style={{ fontSize: '18px', color: theme.colors.textSecondary }}>
                  {quiz_attempt.quiz_title}
                </p>
              </div>
            </DashboardHeader>

            {/* Stats Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '20px',
              marginTop: '24px'
            }}>
              <div style={{
                textAlign: 'center',
                padding: '20px',
                background: theme.colors.surface,
                borderRadius: '12px',
                border: `2px solid ${scoreColor}30`
              }}>
                <div style={{
                  fontSize: '42px',
                  fontWeight: 'bold',
                  color: scoreColor,
                  marginBottom: '8px'
                }}>
                  {quiz_attempt.percentage.toFixed(1)}%
                </div>
                <div style={{
                  fontSize: '14px',
                  color: theme.colors.textSecondary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}>
                  <FiTarget size={16} />
                  Final Score
                </div>
              </div>

              <div style={{
                textAlign: 'center',
                padding: '20px',
                background: theme.colors.surface,
                borderRadius: '12px'
              }}>
                <div style={{
                  fontSize: '42px',
                  fontWeight: 'bold',
                  color: theme.colors.primary,
                  marginBottom: '8px'
                }}>
                  {quiz_attempt.score}/{quiz_attempt.total_points}
                </div>
                <div style={{
                  fontSize: '14px',
                  color: theme.colors.textSecondary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}>
                  <FiStar size={16} />
                  Points Earned
                </div>
              </div>

              <div style={{
                textAlign: 'center',
                padding: '20px',
                background: theme.colors.surface,
                borderRadius: '12px'
              }}>
                <div style={{
                  fontSize: '42px',
                  fontWeight: 'bold',
                  color: '#8B5CF6',
                  marginBottom: '8px'
                }}>
                  {quiz_attempt.time_taken ? Math.floor(quiz_attempt.time_taken / 60) : 0}m
                </div>
                <div style={{
                  fontSize: '14px',
                  color: theme.colors.textSecondary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}>
                  <FiClock size={16} />
                  Time Taken
                </div>
              </div>

              <div style={{
                textAlign: 'center',
                padding: '20px',
                background: theme.colors.surface,
                borderRadius: '12px'
              }}>
                <div style={{
                  fontSize: '42px',
                  fontWeight: 'bold',
                  color: '#06B6D4',
                  marginBottom: '8px'
                }}>
                  #{quiz_attempt.attempt_number}
                </div>
                <div style={{
                  fontSize: '14px',
                  color: theme.colors.textSecondary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}>
                  <FiTrendingUp size={16} />
                  Attempt
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* AI Feature Banner */}
        <div style={{
          background: 'linear-gradient(135deg, #2c5f34 0%, #8B5CF6 100%)',
          color: 'white',
          padding: '20px 24px',
          borderRadius: '12px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.2)',
            padding: '12px',
            borderRadius: '50%'
          }}>
            <FiZap size={24} />
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 4px 0', fontSize: '18px' }}>
              AI-Powered Explanations Available!
            </h3>
            <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>
              Click "Explain with AI" on any question to get detailed, personalized explanations
            </p>
          </div>
        </div>

        {/* Questions Review */}
        <div style={{ marginTop: '32px' }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '600',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <FiBook />
            Question Review
          </h2>

          {questions.map((question, qIndex) => {
            const questionId = question.question_id;
            const isExpanded = expandedQuestions.has(questionId);
            const hasExplanation = !!aiExplanations[questionId];
            const isLoadingExplanation = loadingExplanations[questionId];
            const isCorrect = !!(question.user_selected && question.user_selected.is_correct);
            
            // Check if question has images (either question image or option images)
            const hasQuestionImage = !!question.question_image_url;
            const hasOptionImages = question.options?.some(opt => 
              opt.image_url || opt.option_image_url
            );
            const isImageQuestion = hasQuestionImage || hasOptionImages;

            return (
              <div
                key={questionId}
                style={{
                  background: theme.colors.surface,
                  border: `2px solid ${isCorrect ? '#10B98140' : '#EF444440'}`,
                  borderRadius: '16px',
                  padding: '24px',
                  marginBottom: '20px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}
              >
                {/* Question Header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '16px',
                  marginBottom: '20px'
                }}>
                  {/* Status Icon */}
                  <div style={{
                    minWidth: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: isCorrect ? '#10B98115' : '#EF444415',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {isCorrect ? (
                      <FiCheckCircle size={24} color="#10B981" />
                    ) : (
                      <FiXCircle size={24} color="#EF4444" />
                    )}
                  </div>

                  {/* Question Text */}
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '12px'
                    }}>
                      <span style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        padding: '4px 12px',
                        background: theme.colors.backgroundSecondary,
                        borderRadius: '6px'
                      }}>
                        Question {qIndex + 1}
                      </span>
                      <span style={{
                        fontSize: '14px',
                        color: isCorrect ? '#10B981' : '#EF4444',
                        fontWeight: '600'
                      }}>
                        {question.points_earned}/{question.points_possible} points
                      </span>
                    </div>

                    {/* Question Image (if available) */}
                    {question.question_image_url && (
                      <div style={{ 
                        marginBottom: '20px', 
                        borderRadius: '8px',
                        overflow: 'hidden',
                        background: theme.colors.backgroundSecondary,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '100px'
                      }}>
                        {failedImages.has(`question_${question.question_id}`) ? (
                          <div style={{ 
                            padding: '20px', 
                            textAlign: 'center', 
                            color: theme.colors.textSecondary 
                          }}>
                            🖼️ Image not available
                          </div>
                        ) : (
                          <img 
                            src={question.question_image_url} 
                            alt="Question Image" 
                            style={{ 
                              maxWidth: '100%', 
                              height: 'auto',
                              display: 'block'
                            }}
                            onError={() => {
                              setFailedImages(prev => new Set([...prev, `question_${question.question_id}`]));
                            }}
                          />
                        )}
                      </div>
                    )}

                    {/* Passage (if exists) */}
                    {question.passage_text && (
                      <div style={{
                        background: theme.colors.backgroundSecondary,
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '16px',
                        borderLeft: `4px solid ${
                          question.question_type === 'broken_passage' ? '#F59E0B' : theme.colors.primary
                        }`
                      }}>
                        <div style={{
                          fontSize: '12px',
                          textTransform: 'uppercase',
                          fontWeight: '600',
                          color: question.question_type === 'broken_passage' ? '#F59E0B' : theme.colors.primary,
                          marginBottom: '8px'
                        }}>
                          📖 {question.question_type === 'broken_passage' ? 'Broken Passage' : 'Passage'}
                        </div>
                        <p style={{
                          fontSize: '15px',
                          lineHeight: '1.7',
                          margin: 0,
                          whiteSpace: 'pre-wrap'
                        }}>
                          {question.passage_text}
                        </p>
                      </div>
                    )}

                    {question.question_text && (
                      <p style={{
                        fontSize: '16px',
                        lineHeight: '1.6',
                        margin: '0 0 20px 0',
                        fontWeight: '500'
                      }}>
                        {question.question_text}
                      </p>
                    )}

                    {/* Options */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {question.options.map((option, oIndex) => {
                        const isSelected = option.label === (question.user_selected && question.user_selected.label);
                        const isCorrect = option.is_correct;
                        
                        let backgroundColor = theme.colors.backgroundSecondary;
                        let borderColor = theme.colors.border;
                        let textColor = theme.colors.textPrimary;
                        
                        if (isCorrect) {
                          backgroundColor = '#10B98115';
                          borderColor = '#10B981';
                          textColor = '#10B981';
                        } else if (isSelected && !isCorrect) {
                          backgroundColor = '#EF444415';
                          borderColor = '#EF4444';
                          textColor = '#EF4444';
                        }

                        return (
                          <div
                            key={option.id}
                            style={{
                              padding: '14px 16px',
                              background: backgroundColor,
                              border: `2px solid ${borderColor}`,
                              borderRadius: '10px',
                              display: 'flex',
                              alignItems: (option.image_url || option.option_image_url) ? 'flex-start' : 'center',
                              gap: '12px',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {/* Option Letter */}
                            <div style={{
                              minWidth: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              background: borderColor,
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: '600',
                              fontSize: '14px',
                              marginTop: (option.image_url || option.option_image_url) ? '4px' : '0'
                            }}>
                              {getLetterForIndex(oIndex)}
                            </div>

                            {/* Option Content */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                              {/* Option Image - support both field names */}
                              {(option.image_url || option.option_image_url) && (
                                <div style={{
                                  width: '100%',
                                  maxWidth: '300px',
                                  borderRadius: '8px',
                                  overflow: 'hidden',
                                  background: theme.colors.backgroundSecondary,
                                  minHeight: '60px',
                                  position: 'relative',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  {failedImages.has(`option_${option.id}`) ? (
                                    <div style={{ 
                                      padding: '10px', 
                                      textAlign: 'center', 
                                      color: theme.colors.textSecondary,
                                      fontSize: '24px'
                                    }}>
                                      🖼️
                                    </div>
                                  ) : (
                                    <img 
                                      src={option.image_url || option.option_image_url} 
                                      alt={`Option ${getLetterForIndex(oIndex)}`}
                                      style={{ 
                                        width: '100%', 
                                        height: 'auto',
                                        display: 'block'
                                      }}
                                      onError={() => {
                                        setFailedImages(prev => new Set([...prev, `option_${option.id}`]));
                                      }}
                                    />
                                  )}
                                </div>
                              )}
                              {/* Option Text */}
                              {option.text && (
                                <span style={{
                                  fontSize: '15px',
                                  color: theme.colors.textPrimary
                                }}>
                                  {option.text}
                                </span>
                              )}
                            </div>

                            {/* Indicators */}
                            {isSelected && !isCorrect && (
                              <span style={{
                                fontSize: '12px',
                                padding: '4px 10px',
                                background: '#EF4444',
                                color: 'white',
                                borderRadius: '6px',
                                fontWeight: '600'
                              }}>
                                Your Answer
                              </span>
                            )}
                            {isCorrect && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {isSelected && (
                                  <span style={{
                                    fontSize: '12px',
                                    padding: '4px 10px',
                                    background: '#10B981',
                                    color: 'white',
                                    borderRadius: '6px',
                                    fontWeight: '600'
                                  }}>
                                    Your Answer
                                  </span>
                                )}
                                <FiCheckCircle size={20} color="#10B981" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* AI Explanation Section - Hidden for image questions */}
                {!isImageQuestion && (
                  <div style={{
                    marginTop: '20px',
                    paddingTop: '20px',
                    borderTop: `1px solid ${theme.colors.border}`
                  }}>
                    <Button
                      onClick={() => fetchAIExplanation(questionId)}
                      loading={isLoadingExplanation}
                      disabled={isLoadingExplanation}
                      style={{
                        background: 'linear-gradient(135deg, #2c5f34 0%, #8B5CF6 100%)',
                        color: 'white',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      {(courseId && !ownsCourse(courseId)) ? (
                        <>
                          <FiLock size={18} />
                          Purchase Course for AI Explanation
                        </>
                      ) : isLoadingExplanation ? (
                        <>
                          <div className="spinner" style={{
                            width: '16px',
                            height: '16px',
                            border: '2px solid rgba(255, 255, 255, 0.3)',
                            borderTop: '2px solid white',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }} />
                          Generating Explanation...
                        </>
                      ) : (
                        <>
                          <FiZap size={18} />
                          Explain with AI
                        </>
                      )}
                    </Button>

                    {/* AI Explanation Content */}
                    {/* Explanation now shown in modal */}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom Actions */}
        <div style={{
          marginTop: '32px',
          padding: '24px',
          background: theme.colors.surface,
          borderRadius: '12px',
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          justifyContent: 'center'
        }}>
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <FiArrowLeft /> Back to Quiz
          </Button>
          <Button onClick={() => navigate('/learner/courses')}>
            <FiBook /> Back to Courses
          </Button>
        </div>
      </DashboardContainer>

      {/* AI Explanation Modal */}
      <Modal
        isOpen={isExplanationModalOpen}
        onClose={() => setIsExplanationModalOpen(false)}
        title="AI Explanation"
        size="medium"
      >
        <div style={{ padding: '12px 0' }}>
          {/* Context Badges */}
          {(modalData.userSelected || modalData.correctOption) && (
            <div style={{
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap',
              marginBottom: '12px'
            }}>
              {modalData.userSelected && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 10px',
                  borderRadius: '999px',
                  background: '#EF444415',
                  color: '#EF4444',
                  fontSize: '12px',
                  fontWeight: 600
                }}>
                  Your Answer: {modalData.userSelected.label} — {modalData.userSelected.text}
                </span>
              )}
              {modalData.correctOption && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 10px',
                  borderRadius: '999px',
                  background: '#10B98115',
                  color: '#10B981',
                  fontSize: '12px',
                  fontWeight: 600
                }}>
                  Correct: {modalData.correctOption.label} — {modalData.correctOption.text}
                </span>
              )}
            </div>
          )}

          {/* Question Text */}
          {modalData.questionText && (
            <div style={{
              padding: '12px',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '8px',
              background: theme.colors.backgroundSecondary,
              marginBottom: '12px',
              fontSize: '14px',
              color: theme.colors.textPrimary
            }}>
              <strong>Question:</strong> {modalData.questionText}
            </div>
          )}

          {/* Explanation Body */}
          <div style={{
            padding: '12px',
            border: `1px solid ${theme.colors.primary}40`,
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #2c5f3410 0%, #8B5CF610 100%)',
            color: theme.colors.textPrimary
          }}>
            {renderFormattedMessage(modalData.message)}
          </div>
        </div>
      </Modal>

      {/* Add CSS animation */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </DashboardLayout>
  );
};

export default QuizReviewPage;

